import {
  Badge,
  Button,
  Divider,
  Group,
  Menu,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import React, { useMemo, useState } from 'react';
import { Eye, Template } from 'tabler-icons-react';

import { useGetIssueTemplates } from 'api/issueTemplates';
import { IssueTemplate } from 'types';

type IssueTemplateSelectorProps = {
  projectId?: string;
  issueType: 'task' | 'story' | 'bug' | 'epic' | 'sub-task';
  onSelectTemplate: (template: IssueTemplate) => void;
};

function IssueTemplateSelector({
  projectId,
  issueType,
  onSelectTemplate,
}: IssueTemplateSelectorProps) {
  const [previewTemplate, setPreviewTemplate] = useState<IssueTemplate | null>(null);
  const [previewOpened, setPreviewOpened] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { data: templates = [], isLoading } = useGetIssueTemplates(
    projectId,
    issueType,
    false
  );

  // Group templates by category (project-specific vs org-wide)
  const groupedTemplates = useMemo(() => {
    const projectTemplates: IssueTemplate[] = [];
    const orgTemplates: IssueTemplate[] = [];
    const defaultTemplates: IssueTemplate[] = [];

    templates.forEach((template) => {
      if (template.isDefault) {
        defaultTemplates.push(template);
      } else if (template.project) {
        projectTemplates.push(template);
      } else {
        orgTemplates.push(template);
      }
    });

    return {
      default: defaultTemplates,
      project: projectTemplates,
      org: orgTemplates,
    };
  }, [templates]);

  const handlePreview = (template: IssueTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewTemplate(template);
    setPreviewOpened(true);
  };

  const handleSelect = (template: IssueTemplate) => {
    onSelectTemplate(template);
    setPreviewOpened(false);
    setPreviewTemplate(null);
  };

  if (isLoading || templates.length === 0) {
    return null;
  }

  const totalTemplates =
    groupedTemplates.default.length +
    groupedTemplates.project.length +
    groupedTemplates.org.length;

  return (
    <>
      <Menu shadow="md" width={isMobile ? '100%' : 350} position="bottom-start">
        <Menu.Target>
          <Button
            size="xs"
            variant="subtle"
            leftIcon={<Template size={14} />}
            disabled={templates.length === 0}
          >
            Templates
            {totalTemplates > 0 && ` (${totalTemplates})`}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Issue Templates</Menu.Label>
          <ScrollArea.Autosize
            maxHeight={400}
            placeholder=""
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          >
            {groupedTemplates.default.length > 0 && (
              <>
                <Menu.Label color="dimmed">
                  Default Templates
                </Menu.Label>
                {groupedTemplates.default.map((template) => (
                  <Menu.Item
                    key={template._id}
                    onClick={() => handleSelect(template)}
                    rightSection={
                      <Group spacing={4}>
                        <Tooltip label="Preview template">
                          <Button
                            size="xs"
                            variant="subtle"
                            compact
                            onClick={(e) => handlePreview(template, e)}
                          >
                            <Eye size={12} />
                          </Button>
                        </Tooltip>
                        <Badge size="xs" color="violet">
                          Default
                        </Badge>
                      </Group>
                    }
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" weight={600} truncate>
                        {template.name}
                      </Text>
                      {template.description && (
                        <Text size="xs" color="dimmed" truncate>
                          {template.description}
                        </Text>
                      )}
                    </div>
                  </Menu.Item>
                ))}
                {(groupedTemplates.project.length > 0 ||
                  groupedTemplates.org.length > 0) && <Divider my="xs" />}
              </>
            )}

            {groupedTemplates.project.length > 0 && (
              <>
                <Menu.Label color="dimmed">
                  Project Templates
                </Menu.Label>
                {groupedTemplates.project.map((template) => (
                  <Menu.Item
                    key={template._id}
                    onClick={() => handleSelect(template)}
                    rightSection={
                      <Tooltip label="Preview template">
                        <Button
                          size="xs"
                          variant="subtle"
                          compact
                          onClick={(e) => handlePreview(template, e)}
                        >
                          <Eye size={12} />
                        </Button>
                      </Tooltip>
                    }
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" weight={400} truncate>
                        {template.name}
                      </Text>
                      {template.description && (
                        <Text size="xs" color="dimmed" truncate>
                          {template.description}
                        </Text>
                      )}
                      {template.project && (
                        <Text size="xs" color="blue" truncate>
                          {template.project.name}
                        </Text>
                      )}
                    </div>
                  </Menu.Item>
                ))}
                {groupedTemplates.org.length > 0 && <Divider my="xs" />}
              </>
            )}

            {groupedTemplates.org.length > 0 && (
              <>
                <Menu.Label color="dimmed">
                  Organization Templates
                </Menu.Label>
                {groupedTemplates.org.map((template) => (
                  <Menu.Item
                    key={template._id}
                    onClick={() => handleSelect(template)}
                    rightSection={
                      <Tooltip label="Preview template">
                        <Button
                          size="xs"
                          variant="subtle"
                          compact
                          onClick={(e) => handlePreview(template, e)}
                        >
                          <Eye size={12} />
                        </Button>
                      </Tooltip>
                    }
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" weight={400} truncate>
                        {template.name}
                      </Text>
                      {template.description && (
                        <Text size="xs" color="dimmed" truncate>
                          {template.description}
                        </Text>
                      )}
                    </div>
                  </Menu.Item>
                ))}
              </>
            )}
          </ScrollArea.Autosize>
        </Menu.Dropdown>
      </Menu>

      {/* Template Preview Modal */}
      <Modal
        opened={previewOpened}
        onClose={() => {
          setPreviewOpened(false);
          setPreviewTemplate(null);
        }}
        title={
          <Group spacing="xs">
            <Template size={18} />
            <Text weight={600}>
              {previewTemplate?.name || 'Template Preview'}
            </Text>
          </Group>
        }
        size="md"
        centered
      >
        {previewTemplate && (
          <Stack spacing="md">
            {previewTemplate.description && (
              <Text size="sm" color="dimmed">
                {previewTemplate.description}
              </Text>
            )}

            <Divider />

            <div>
              <Text size="sm" weight={600} mb="xs">
                Template Fields:
              </Text>
              <Paper withBorder p="md" radius="md">
                <Stack spacing="xs">
                  {previewTemplate.templateData.title && (
                    <div>
                      <Text size="xs" color="dimmed" weight={500}>
                        Title:
                      </Text>
                      <Text size="sm">{previewTemplate.templateData.title}</Text>
                    </div>
                  )}

                  {previewTemplate.templateData.description && (
                    <div>
                      <Text size="xs" color="dimmed" weight={500}>
                        Description:
                      </Text>
                      <Text
                        size="sm"
                        dangerouslySetInnerHTML={{
                          __html: previewTemplate.templateData.description,
                        }}
                      />
                    </div>
                  )}

                  {previewTemplate.templateData.priority && (
                    <div>
                      <Text size="xs" color="dimmed" weight={500}>
                        Priority:
                      </Text>
                      <Badge
                        color={
                          (() => {
                            if (previewTemplate.templateData.priority === 'high') return 'red';
                            if (previewTemplate.templateData.priority === 'medium') return 'yellow';
                            return 'blue';
                          })()
                        }
                        variant="light"
                      >
                        {previewTemplate.templateData.priority}
                      </Badge>
                    </div>
                  )}

                  {previewTemplate.templateData.labels &&
                    previewTemplate.templateData.labels.length > 0 && (
                      <div>
                        <Text size="xs" color="dimmed" weight={500}>
                          Labels:
                        </Text>
                        <Group spacing={4} mt={4}>
                          {previewTemplate.templateData.labels.map((label) => (
                            <Badge key={`label-${label}`} size="sm" variant="dot">
                              {label}
                            </Badge>
                          ))}
                        </Group>
                      </div>
                    )}

                  {previewTemplate.templateData.components &&
                    previewTemplate.templateData.components.length > 0 && (
                      <div>
                        <Text size="xs" color="dimmed" weight={500}>
                          Components:
                        </Text>
                        <Group spacing={4} mt={4}>
                          {previewTemplate.templateData.components.map((comp) => (
                            <Badge key={`component-${comp}`} size="sm" color="violet" variant="light">
                              {comp}
                            </Badge>
                          ))}
                        </Group>
                      </div>
                    )}

                  {previewTemplate.templateData.fixVersions &&
                    previewTemplate.templateData.fixVersions.length > 0 && (
                      <div>
                        <Text size="xs" color="dimmed" weight={500}>
                          Fix Versions:
                        </Text>
                        <Group spacing={4} mt={4}>
                          {previewTemplate.templateData.fixVersions.map((version) => (
                            <Badge key={`version-${version}`} size="sm" color="green" variant="light">
                              {version}
                            </Badge>
                          ))}
                        </Group>
                      </div>
                    )}

                  {previewTemplate.templateData.environment && (
                    <div>
                      <Text size="xs" color="dimmed" weight={500}>
                        Environment:
                      </Text>
                      <Badge size="sm" color="cyan" variant="light" mt={4}>
                        {previewTemplate.templateData.environment}
                      </Badge>
                    </div>
                  )}

                  {previewTemplate.templateData.dueDate && (
                    <div>
                      <Text size="xs" color="dimmed" weight={500}>
                        Due Date:
                      </Text>
                      <Text size="sm" mt={4}>
                        {new Date(previewTemplate.templateData.dueDate).toLocaleDateString()}
                      </Text>
                    </div>
                  )}
                </Stack>
              </Paper>
            </div>

            <Group position="right" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  setPreviewOpened(false);
                  setPreviewTemplate(null);
                }}
              >
                Close
              </Button>
              <Button onClick={() => handleSelect(previewTemplate)}>
                Use Template
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}

export default IssueTemplateSelector;
